import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Group, Stack, Text } from "@mantine/core";

import ButtonVariants from "@core-components/button/ButtonVariants";
import Icon from "@core-components/icon/Icon";
import COrderStatus from "@modules/order/models/COrderStatus";
import type IOrder from "@modules/order/models/IOrder";
import {
  BOLD_TEXT_CLASS_NAME,
  LIGHT_TEXT_CLASS_NAME,
} from "@modules/order/pages/OrderTracking";
import OrderStatus from "./OrderStatus";
import SSSection from "./Section";

interface IOrderDetailsProps {
  order: IOrder | undefined;
  updateOrder: () => void;
  cancelOrder: () => void;
  recreateOrder: () => void;
}

const OrderDetails: React.FC<IOrderDetailsProps> = ({
  order,
  updateOrder,
  cancelOrder,
  recreateOrder,
}) => {
  const { t } = useAppTranslation("pages.order.tracking.summary");
  const { t: tProduct } = useAppTranslation();

  return (
    <SSSection icon={<Icon.OrderConfirmed size={13} />} title={t("title")}>
      <Stack className="w-full">
        <Flex className="xs:flex-row flex-col xs:justify-between sm:flex-col lg:flex-row">
          <Text className={BOLD_TEXT_CLASS_NAME}>{t("purchase")}</Text>
          <Stack gap={10} className="items-end">
            {order?.accounts.map((item) => (
              <Text
                key={item.accountId}
                className={`${LIGHT_TEXT_CLASS_NAME} text-end`}
              >
                {tProduct(`${item.i18nKey}.basket.modalTitle`, {
                  prefix: item.prefix,
                })}
              </Text>
            ))}
          </Stack>
        </Flex>
        <Flex className="xs:flex-row flex-col xs:justify-between sm:flex-col lg:flex-row">
          <Text className={BOLD_TEXT_CLASS_NAME}>{t("orderId")}</Text>
          <Text className={LIGHT_TEXT_CLASS_NAME}>{order?.token ?? "--"}</Text>
        </Flex>
        {order?.referrer && (
          <Flex className="xs:flex-row flex-col xs:justify-between sm:flex-col lg:flex-row">
            <Text className={BOLD_TEXT_CLASS_NAME}>{t("referrer")}</Text>
            <Group gap={5}>
              <Text className={LIGHT_TEXT_CLASS_NAME}>
                {order?.referrer?.email} {t("heReceived")}
              </Text>
              <Text
                className={`${LIGHT_TEXT_CLASS_NAME} !text-coins-text !font-medium`}
              >
                {order?.referrer?.coinsGained} {t("coins")}
              </Text>
            </Group>
          </Flex>
        )}
        <Flex className="xs:flex-row flex-col xs:justify-between sm:flex-col lg:flex-row">
          <Text className={BOLD_TEXT_CLASS_NAME}>{t("status")}</Text>

          <OrderStatus
            status={order?.status ?? new COrderStatus(COrderStatus.PENDING)}
          />
        </Flex>
        <Flex className="xs:flex-row flex-col xs:justify-between sm:flex-col lg:flex-row">
          <Text className={BOLD_TEXT_CLASS_NAME}>{t("email")}</Text>
          <Text className={LIGHT_TEXT_CLASS_NAME}>{order?.email}</Text>
        </Flex>
        <Flex className="xs:flex-row flex-col xs:justify-between sm:flex-col lg:flex-row">
          <Text className={BOLD_TEXT_CLASS_NAME}>{t("total")}</Text>
          <Text className={LIGHT_TEXT_CLASS_NAME}>
            {order?.price ?? "--"} €
          </Text>
        </Flex>
        {order?.status.isPending() && (
          <Group gap={10} className="justify-center">
            <ButtonVariants.Gradient
              className="hover-scale"
              label={t("modify")}
              onClick={updateOrder}
            />
            <ButtonVariants.Basic
              className="hover-scale border-none bg-red-500 text-primary-white hover:bg-red-600 hover:text-primary-white"
              label={t("cancel")}
              fullWidth={false}
              onClick={cancelOrder}
            />
          </Group>
        )}
        {order?.status.isExpired() && (
          <ButtonVariants.Gradient
            className="hover-scale"
            label={t("recreate")}
            onClick={recreateOrder}
          />
        )}
      </Stack>
    </SSSection>
  );
};

export default OrderDetails;
